/**
 * Checkout contact-related logic: validation and contact mapping.
 */
import { useCallback } from "react";
import { validateEmail } from "../utils";
import type {
  CheckoutState,
  CheckoutContact,
  ContactEntity,
} from "../components/shop/Checkout/types";

interface UseCheckoutContactArgs {
  state: CheckoutState;
}

export function useCheckoutContact({ state }: UseCheckoutContactArgs) {
  const checkContactErrors = useCallback(
    (field: string): boolean => {
      function check(f: string): boolean {
        if (f.includes("shipping") && state.sameAsBilling) {
          return check(f.replace("shipping", "billing"));
        }
        const fieldId = state[`${f}_id` as keyof CheckoutState] as
          | string
          | undefined;
        const contact = state[f as keyof CheckoutState] as
          | CheckoutContact
          | undefined;
        const hasId = fieldId && fieldId !== "ADD";
        const hasTyped =
          contact?.contact_name?.trim() &&
          validateEmail(contact?.contact_email?.trim() ?? "");
        return !(hasId || hasTyped);
      }
      return check(field);
    },
    [state],
  );

  const mapSelectContact = useCallback(
    (c: ContactEntity) => ({
      ...c,
      key: c.contact_id,
      value: `${c.contact_first_name ?? ""} ${
        c.contact_last_name ?? ""
      }`.trim(),
    }),
    [],
  );

  return { checkContactErrors, mapSelectContact };
}
